import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Error } from "@mui/icons-material";
import { CardContent, Typography, Box } from "@mui/material";
import { MuiButton, MuiCard, ACEcolors } from "@ace/ace-ui-web";
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { activateUser, clearActivationState } from '../../redux/RegistrationReducer/registrationSlice';
import AuthHeader from '../../components/auth-header/AuthHeader';
import styles from "./activate.module.css";

const REDIRECT_DELAY_SECONDS = 5;

type ActivationState =
    | { kind: "missingToken" }
    | { kind: "ready" }
    | { kind: "activating" }
    | { kind: "success" }
    | { kind: "error" };

const ActivatePage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const { activating, activationSuccess, activationError, activationMessage } = useAppSelector(
        (state) => state.registration
    );

    const [countdown, setCountdown] = useState(REDIRECT_DELAY_SECONDS);
    const token = searchParams.get('token')?.trim() ?? '';

    const [localState, setLocalState] = useState<ActivationState>(
        token ? { kind: "ready" } : { kind: "missingToken" }
    );

    useEffect(() => {
        if (activating) {
            setLocalState({ kind: "activating" });
        } else if (activationSuccess) {
            setLocalState({ kind: "success" });
        } else if (activationError) {
            setLocalState({ kind: "error" });
        } else if (token) {
            setLocalState({ kind: "ready" });
        } else {
            setLocalState({ kind: "missingToken" });
        }
    }, [activating, activationSuccess, activationError, token]);

    useEffect(() => {
        return () => {
            dispatch(clearActivationState());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!activationSuccess) return;

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    navigate('/login', { replace: true });
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [activationSuccess, navigate]);

    const handleActivate = async () => {
        if (!token || activating) return;
        dispatch(activateUser({ token }));
    };

    const handleGoToLogin = () => {
        navigate('/login', { replace: true });
    };

    const handleBackToHome = () => {
        navigate('/', { replace: true });
    };

    const renderContent = () => {
        if (localState.kind === "missingToken") {
            return (
                <ActivationCard
                    icon={<Error sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />}
                    title="Invalid Activation Link"
                    message="No activation token was provided. Please use the link from your activation email."
                    primaryButton={{ label: "Back to Home", onClick: handleBackToHome }}
                />
            );
        }

        if (localState.kind === "ready") {
            return (
                <ActivationCard
                    icon={<CheckCircle sx={{ fontSize: 64, color: ACEcolors.primary.default, mb: 2 }} />}
                    title="Activate Your MyAceFlex Account"
                    message="Click the button below to finish activating your account."
                    subMessage="This link expires 20 minutes after registration."
                    primaryButton={{ label: "Activate My Account", onClick: handleActivate }}
                    isActivating={activating}
                />
            );
        }

        if (localState.kind === "activating") {
            return (
                <MuiCard borderRadius={2} elevation={5} padding={1} variant="elevation">
                    <CardContent>
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                style={{ display: 'inline-block' }}
                            >
                                <Box sx={{ 
                                    width: 64, 
                                    height: 64, 
                                    borderRadius: '50%', 
                                    border: `4px solid ${ACEcolors.primary.default}`,
                                    borderTopColor: 'transparent',
                                    mb: 3
                                }} />
                            </motion.div>
                            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
                                Activating Your Account
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Please wait while we verify your activation link...
                            </Typography>
                        </Box>
                    </CardContent>
                </MuiCard>
            );
        }

        if (localState.kind === "success") {
            return (
                <ActivationCard
                    icon={<CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />}
                    title="Account Activated!"
                    message={activationMessage || "Your account has been successfully activated."}
                    subMessage={`Redirecting you to login in ${countdown} seconds...`}
                    primaryButton={{ label: "Go to Login", onClick: handleGoToLogin }}
                />
            );
        }

        if (localState.kind === "error") {
            return (
                <ActivationCard
                    icon={<Error sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />}
                    title="Activation Failed"
                    message={activationError || "We couldn't activate your account. Please request a new activation link."}
                    primaryButton={{ label: "Go to Login", onClick: handleGoToLogin }}
                    secondaryButton={{ label: "Back to Home", onClick: handleBackToHome }}
                />
            );
        }

        return null;
    };

    return (
        <div className={styles.appContainer}>
            <div className={styles.contentWrapper}>
                <AuthHeader className={styles.header} />
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    {renderContent()}
                </motion.div>
            </div>
        </div>
    );
};

interface ActivationCardProps {
    icon: React.ReactNode;
    title: string;
    message: string;
    subMessage?: string;
    primaryButton: { label: string; onClick: () => void };
    secondaryButton?: { label: string; onClick: () => void };
    isActivating?: boolean;
}

const ActivationCard: React.FC<ActivationCardProps> = ({
    icon,
    title,
    message,
    subMessage,
    primaryButton,
    secondaryButton,
    isActivating = false,
}) => (
    <MuiCard borderRadius={2} elevation={5} padding={1} variant="elevation">
        <CardContent>
            <Box sx={{ textAlign: 'center', py: 3 }}>
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                >
                    {icon}
                </motion.div>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {title}
                </Typography>

                <Typography variant="body1" sx={{ color: 'text.secondary', mb: subMessage ? 2 : 4 }}>
                    {message}
                </Typography>

                {subMessage && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
                        {subMessage}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <MuiButton
                        size="large"
                        onClick={primaryButton.onClick}
                        disabled={primaryButton.label === "Activate My Account" && isActivating}
                        sx={{ width: '100%' }}
                    >
                        {primaryButton.label}
                    </MuiButton>
                    {secondaryButton && (
                        <MuiButton
                            variant="outlined"
                            size="large"
                            onClick={secondaryButton.onClick}
                            sx={{ width: '100%' }}
                        >
                            {secondaryButton.label}
                        </MuiButton>
                    )}
                </Box>
            </Box>
        </CardContent>
    </MuiCard>
);

export default ActivatePage;